var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useMemo, useEffect } from 'react';
import { useHttpState } from './useHttpState';
var createUnleashClient = function (baseURL) {
    if (!baseURL) {
        return null;
    }
    return {
        getFlags: function (accessToken, tenantId) { return __awaiter(void 0, void 0, void 0, function () {
            var response, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, fetch("".concat(baseURL), {
                            method: 'POST',
                            headers: {
                                'content-type': 'application/x-www-form-urlencoded',
                            },
                            body: new URLSearchParams({
                                accessToken: accessToken,
                                userProperties: JSON.stringify({
                                    orgId: tenantId,
                                }),
                            }),
                        })];
                    case 1:
                        response = _a.sent();
                        if (!response.ok) {
                            throw new Error("Failed to fetch feature flags: ".concat(response.status, " ").concat(response.statusText));
                        }
                        return [4 /*yield*/, response.json()];
                    case 2:
                        data = (_a.sent());
                        // Type guard to verify the response matches FeatureFlagUnleashedValue
                        if (typeof data === 'object' && data !== null) {
                            return [2 /*return*/, Object.fromEntries(Object.entries(data).map(function (_a) {
                                    var _b = __read(_a, 2), k = _b[0], v = _b[1];
                                    if (typeof v === 'boolean') {
                                        return [k, v];
                                    }
                                    throw new Error('Invalid feature flag response format');
                                }))];
                        }
                        throw new Error('Invalid feature flag response format');
                }
            });
        }); },
    };
};
var useUnleashClient = function (baseUrl) {
    return useMemo(function () { return createUnleashClient(baseUrl); }, [baseUrl]);
};
// NOTE: The implementation of this hook is in the FeatureFlagContext.tsx file.
// This hook itself is not meant to be used directly.
export var useFeatureFlagService = function (serviceUrl, accessToken, tenantId) {
    var client = useUnleashClient(serviceUrl);
    var _a = useHttpState(), data = _a.data, error = _a.error, loading = _a.loading, setLoading = _a.setLoading, setData = _a.setData, setError = _a.setError;
    useEffect(function () {
        // Don't make request if required parameters are missing
        if (!tenantId || !accessToken || !serviceUrl || !client) {
            return;
        }
        setLoading();
        var controller = new AbortController();
        client
            .getFlags(accessToken, tenantId)
            .then(function (response) {
            if (!controller.signal.aborted) {
                setData(response);
            }
        })
            .catch(function (error) {
            if (!controller.signal.aborted) {
                setError(error);
            }
        });
        return function () {
            controller.abort();
        };
    }, [
        tenantId,
        accessToken,
        client,
        setData,
        setError,
        setLoading,
        serviceUrl,
    ]);
    return {
        data: data,
        error: error,
        loading: loading,
    };
};
